// ── Auto-Director ──
// Automatically selects the most interesting player to spectate based on
// scoring heuristics: recent kills, damage, clutch potential, bomb proximity.

import { type RemotePlayer } from '../gameplay/player';

export class AutoDirector {
  private enabled = false;
  private currentTarget = 0;
  private lastSwitchTime = 0;
  private readonly minDwellTime = 3000; // ms — don't switch faster than 3s
  private scores: Map<number, number> = new Map();
  /** Tracks whether bomb was planted in the previous frame for edge detection. */
  private wasBombPlanted = false;

  constructor() {}

  /** Toggle auto-director on/off. Returns new state. */
  toggle(): boolean {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.scores.clear();
    }
    return this.enabled;
  }

  /**
   * Update scores and potentially switch target.
   * Call each frame with current player data and recent events.
   * Returns the recommended target player ID (0 = no change).
   */
  update(
    players: Map<number, any>,
    events: Array<{ type: string; data: any }>,
    bombPlanted: boolean,
    bombPosition: { x: number; z: number } | null,
  ): number {
    if (!this.enabled) return 0;

    const now = Date.now();
    this.scores.clear();

    // Score each alive player
    for (const [id, player] of players) {
      if (player.health <= 0) {
        this.scores.set(id, 0);
        continue;
      }

      let score = 5; // base score for being alive

      // Check events for this player
      for (const event of events) {
        if (event.type === 'kill' && event.data.killer === id) {
          const eventTime = event.data.time ?? now;
          if (now - eventTime < 2000) {
            score += 50; // recent kill
          }
        }
        if (event.type === 'damage' && event.data.attacker === id) {
          const eventTime = event.data.time ?? now;
          if (now - eventTime < 2000) {
            score += 20; // recent damage dealt
          }
        }
      }

      // Low HP clutch potential
      if (player.health > 0 && player.health < 30) {
        score += 15;
      }

      // Near bomb when planted
      if (bombPlanted && bombPosition && player.position) {
        const dx = player.position.x - bombPosition.x;
        const dz = player.position.z - bombPosition.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < 10) {
          score += 25;
        }
      }

      this.scores.set(id, score);
    }

    // Detect new bomb plant (edge: was not planted, now is)
    const bombJustPlanted = bombPlanted && !this.wasBombPlanted;
    this.wasBombPlanted = bombPlanted;

    // On bomb plant: immediately switch to nearest alive player to bomb
    if (bombJustPlanted && bombPosition) {
      let nearestId = 0;
      let nearestDist = Infinity;
      for (const [id, player] of players) {
        if (player.health <= 0) continue;
        if (!player.position) continue;
        const dx = player.position.x - bombPosition.x;
        const dz = player.position.z - bombPosition.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestId = id;
        }
      }
      if (nearestId > 0 && nearestId !== this.currentTarget) {
        this.currentTarget = nearestId;
        this.lastSwitchTime = now;
        return nearestId;
      }
    }

    // Find the highest-scoring player
    let bestId = 0;
    let bestScore = -1;
    for (const [id, score] of this.scores) {
      if (score > bestScore) {
        bestScore = score;
        bestId = id;
      }
    }

    // If no current target, pick the best immediately
    if (this.currentTarget === 0 && bestId > 0) {
      this.currentTarget = bestId;
      this.lastSwitchTime = now;
      return bestId;
    }

    // Check if current target is still alive
    const currentPlayer = players.get(this.currentTarget);
    if (!currentPlayer || currentPlayer.health <= 0) {
      // Current target is dead — switch immediately
      if (bestId > 0) {
        this.currentTarget = bestId;
        this.lastSwitchTime = now;
        return bestId;
      }
      return 0;
    }

    // Dwell time check
    if (now - this.lastSwitchTime < this.minDwellTime) {
      return 0;
    }

    // Hysteresis: only switch if new target's score exceeds current by > 10
    const currentScore = this.scores.get(this.currentTarget) ?? 0;
    if (bestId !== this.currentTarget && bestScore > currentScore + 10) {
      this.currentTarget = bestId;
      this.lastSwitchTime = now;
      return bestId;
    }

    return 0;
  }

  /** Get current target. */
  get target(): number {
    return this.currentTarget;
  }

  /** Check if auto-director is active. */
  get isActive(): boolean {
    return this.enabled;
  }

  /** Reset state (new round). */
  reset(): void {
    this.currentTarget = 0;
    this.lastSwitchTime = 0;
    this.scores.clear();
    this.wasBombPlanted = false;
  }
}
